import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { COURSES } from "../../data/content";

const PROFILES = ["Contador independiente", "Empresa / equipo contable", "Estudiante", "Otro profesional"];
const PLANS_OPTS = ["Pago único", "3 cuotas sin interés"];
const COURSE_OPTS = [...COURSES.map((c) => c.title), "Pase Master Trimestral", "Plan Corporativo"];

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all";

export const EnrollmentModal = ({ open, preset, onClose }) => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", profile: PROFILES[0], course: COURSE_OPTS[0], plan: PLANS_OPTS[0] });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm((f) => ({
        ...f,
        course: preset?.course || COURSE_OPTS[0],
        plan: preset?.plan || PLANS_OPTS[0],
      }));
    }
  }, [open, preset]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/enrollments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      toast.success("¡Inscripción recibida! Te contactaremos en menos de 24 horas.");
    } catch {
      toast.error("No pudimos registrar tu inscripción. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="enrollment-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl border border-border bg-card p-7 sm:p-9 shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <button data-testid="enrollment-close-btn" onClick={onClose} aria-label="Cerrar" className="absolute top-5 right-5 w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>

            {done ? (
              <div data-testid="enrollment-success" className="text-center py-10">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }}>
                  <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
                </motion.div>
                <h3 className="font-display text-2xl font-bold tracking-tight mt-5">¡Cupo reservado!</h3>
                <p className="text-sm text-muted-foreground mt-3 max-w-xs mx-auto leading-relaxed">
                  Recibimos tu inscripción a <strong className="text-foreground">{form.course}</strong>. Te escribiremos a {form.email} con el link de pago y la bienvenida.
                </p>
                <button data-testid="enrollment-done-btn" onClick={onClose} className="mt-7 rounded-full bg-primary text-primary-foreground font-semibold px-8 py-3 text-sm hover:brightness-110 transition-all">
                  Entendido
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase font-mono tracking-[0.25em] text-emerald-500 font-semibold">Reserva tu cupo</p>
                <h3 className="font-display text-2xl font-bold tracking-tight mt-2">Inscripción al programa</h3>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <Field label="Nombre completo" id="enroll-name">
                    <input id="enroll-name" data-testid="enrollment-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Ej: Laura Gómez Pérez" className={inputCls} />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Correo electrónico" id="enroll-email">
                      <input id="enroll-email" data-testid="enrollment-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="tu@correo.com" className={inputCls} />
                    </Field>
                    <Field label="WhatsApp" id="enroll-phone">
                      <input id="enroll-phone" data-testid="enrollment-phone-input" required value={form.phone} onChange={set("phone")} placeholder="+57 300 000 0000" className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Tu perfil" id="enroll-profile">
                    <select id="enroll-profile" data-testid="enrollment-profile-select" value={form.profile} onChange={set("profile")} className={inputCls}>
                      {PROFILES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Curso / plan" id="enroll-course">
                      <select id="enroll-course" data-testid="enrollment-course-select" value={form.course} onChange={set("course")} className={inputCls}>
                        {COURSE_OPTS.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="Forma de pago" id="enroll-plan">
                      <select id="enroll-plan" data-testid="enrollment-plan-select" value={form.plan} onChange={set("plan")} className={inputCls}>
                        {PLANS_OPTS.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </Field>
                  </div>
                  <button
                    data-testid="enrollment-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-4 text-sm hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? "Registrando..." : "Confirmar inscripción"}
                  </button>
                  <p className="text-[11px] text-muted-foreground text-center">Sin cargos automáticos. Un asesor confirmará tu cupo y el pago.</p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
